import React, { useState } from 'react';
import './SessionSetup.css';

export interface SessionConfig {
  specialty: string;
  rarity: string;
}

interface SessionSetupProps {
  onStart: (config: SessionConfig) => void;
  planKey?: string;
}

const SPECIALTIES = [
  { id: 'cardiologia', label: 'Cardiologia', desc: 'Arritmias complexas, valvulopatias e insuficiência cardíaca aguda.', icon: 'cardiology' },
  { id: 'neurologia', label: 'Neurologia', desc: 'Sinais focais e cefaleias.', icon: 'neurology' },
  { id: 'pneumologia', label: 'Pneumologia', desc: 'Doenças obstrutivas.', icon: 'pulmonology' },
  { id: 'gastroenterologia', label: 'Gastroenterologia', desc: 'Abdome agudo e DII.', icon: 'gastroenterology' },
  { id: 'infectologia', label: 'Infectologia', desc: 'Febres e antibiogramas.', icon: 'microbiology' },
  { id: 'endocrinologia', label: 'Endocrinologia', desc: 'Eixo hormonal e DM.', icon: 'endocrinology' },
  { id: 'nefrologia', label: 'Nefrologia', desc: 'IRA e distúrbios eletrolíticos.', icon: 'nephrology' },
  { id: 'emergencia', label: 'Emergência', desc: 'Protocolos de trauma e choque.', icon: 'emergency' },
  { id: 'reumatologia', label: 'Reumatologia', desc: 'Autoimunidade sistêmica.', icon: 'skeleton' },
];

const RARITIES = [
  { id: 'comum', label: 'Comum', level: 'Nível 1' },
  { id: 'incomum', label: 'Incomum', level: 'Nível 2' },
  { id: 'rara', label: 'Rara', level: 'Nível 3' },
  { id: 'aleatorio', label: 'Aleatório', level: null },
];

const SessionSetup: React.FC<SessionSetupProps> = ({ onStart, planKey }) => {
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [selectedRarity, setSelectedRarity] = useState('');

  const canStart = selectedSpecialty !== '' && selectedRarity !== '';

  return (
    <main className="flex-grow pt-8 pb-8 px-8 max-w-7xl mx-auto w-full">
      {/* Hero Section */}
      <div className="mb-10">
        <div>
          <span className="inline-block py-1 px-3 rounded-full bg-surface-container-highest text-primary font-label text-[10px] uppercase tracking-widest mb-4">Módulo de Simulação v4.0</span>
          <h1 className="text-5xl md:text-7xl font-headline font-extrabold tracking-tighter text-on-background leading-none mb-6">
            Selecione uma <br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Especialidade</span>
          </h1>
        </div>
      </div>

      {/* Specialty Grid - Bento Style */}
      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-10">
        {SPECIALTIES.map((s) => {
          const isSelected = selectedSpecialty === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setSelectedSpecialty(s.id)}
              aria-pressed={isSelected}
              className={`relative group overflow-hidden rounded-2xl bg-surface-container-low p-6 text-left transition-all hover:bg-surface-bright ${isSelected ? 'ring-2 ring-primary' : ''}`}
            >
              <div className="h-12 w-12 rounded-xl bg-primary-container/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>{s.icon}</span>
              </div>
              <h3 className="text-lg font-headline font-bold text-on-surface">{s.label}</h3>
            </button>
          );
        })}
      </section>

      {/* Difficulty Selection & CTA */}
      <section className="flex flex-col sm:flex-row items-start gap-8">
        <div className="flex-1 max-w-md">
          <h4 className="text-xl font-headline font-bold text-on-surface mb-6 flex items-center gap-2">
            <span className="material-symbols-outlined text-primary-container">tune</span>
            Dificuldade do Caso
          </h4>
          <div className="flex flex-col gap-3">
            {RARITIES.map((r) => {
              const isChecked = selectedRarity === r.id;
              const isRare = r.id === 'rara';
              const isRandom = r.id === 'aleatorio';
              return (
                <label
                  key={r.id}
                  className="group flex items-center justify-between p-4 rounded-xl bg-surface-container-low cursor-pointer hover:bg-surface-container-high transition-all"
                  onClick={() => setSelectedRarity(r.id)}
                >
                  <span className="flex items-center gap-3">
                    <input
                      checked={isChecked}
                      className="w-4 h-4 text-primary bg-surface-container-highest border-none focus:ring-offset-0 focus:ring-primary"
                      name="difficulty"
                      type="radio"
                      value={r.id}
                      onChange={() => setSelectedRarity(r.id)}
                    />
                    <span className="text-on-surface font-label font-medium">{r.label}</span>
                  </span>
                  {isRandom ? (
                    <span className="material-symbols-outlined text-on-surface-variant text-sm">shuffle</span>
                  ) : (
                    <span className={`text-[10px] text-on-surface-variant uppercase tracking-tighter ${isRare ? 'font-bold text-primary' : ''}`}>{r.level}</span>
                  )}
                </label>
              );
            })}
          </div>
        </div>
        <div className="sm:pt-12">
          <button
            type="button"
            disabled={!canStart}
            onClick={() => onStart({ specialty: selectedSpecialty, rarity: selectedRarity })}
            className={`bg-gradient-to-r from-primary to-primary-container text-on-primary-container text-lg font-headline font-bold px-12 py-5 rounded-full shadow-2xl shadow-sky-500/30 hover:shadow-sky-400/50 hover:scale-[1.02] active:scale-95 transition-all duration-300 ${!canStart ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Iniciar Sessão Diagnóstica
          </button>
        </div>
      </section>
    </main>
  );
};

export default SessionSetup;
